import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Button from '@ingka/button';
import useTranslation from '../../hooks/useTranslation';
import {
  validateInvalidCharacters,
  validateZip,
  validateName,
  validatestreetAddress,
  validatestreetAddress2,
  validateCity,
  validatePhoneNumber,
  validateEmail,
  autoFormatNumber,
  validateState,
  validateReason,
  validateDistrict,
  validateBlockNumber,
} from './validations';
import TermsAndConditionsRenderer from './TermsAndConditionsRenderer';
import { RenderFields } from '../RenderFields';
import { getCountryDisplayName } from './utils';
import CheckboxUpdateProfile from '../CheckboxUpdateProfile';
import {
  CityResponseType,
  DistrictResponseType,
  sparePartService,
} from '../../services';
import cssIngka from '@ingka/variables/colours-css';
import {
  remapValuesToReadable,
  renameObjectKeys,
  appendBlock,
} from '../../helpers/renameObjects';
import {
  AddressAutoCompleteComponent,
  AddressDetails,
} from './AddressAutoCompleteComponent';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { useCart } from '../../contexts/Cart';
import InlineMessage from '@ingka/inline-message';
import noticeCircle from '@ingka/ssr-icon/paths/notice-circle';

const StyledInputFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;
const StyledTOSWrapper = styled.div`
  margin: 40px 0 60px;
`;

const StyledAddressContactUsWrapper = styled.div`
  margin: 20px 0 30px;
`;

const ConfirmationBox = styled.div`
  margin: 0.4rem 0;
  span {
    display: inline-block;
    margin-top: 1rem;
  }
`;

const StyledH2 = styled.h2.attrs({ className: 'h3' })`
  font-weight: bold;
  color: ${cssIngka.colourNeutral7};
  margin-bottom: 2px;
`;

const StyledFieldSet = styled.fieldset`
  border: none; /* Remove default fieldset border */
  padding: 0;
  margin-top: 10px;
  margin-bottom: 20px;
`;

const StyledLegend = styled.legend`
  font-weight: 400; /* Ensure bold text */
  font-size: 0.8125rem;
  line-height: 1.571;
  color: rgb(var(--colour-text-and-icon-2, 72, 72, 72));
  margin-bottom: -0.1rem; /* Add spacing between legend and buttons */
  display: block;
`;

const StyledRadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 5px 0 10px 0;
`;

const StyledRadioLabel = styled.label<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  flex: 1;
  min-width: 150px;
  padding: 12px 16px;
  border: 1px solid
    ${({ $isSelected }) =>
      $isSelected
        ? cssIngka.colourStaticIkeaBrandBlue
        : cssIngka.colourNeutral3};
  border-radius: 4px;
  background-color: ${({ $isSelected }) =>
    $isSelected ? cssIngka.colourStaticIkeaBrandBlue : cssIngka.colourNeutral1};
  transition: all 0.2s ease-in-out;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ $isSelected }) =>
    $isSelected ? cssIngka.colourNeutral1 : cssIngka.colourNeutral7};

  /* Hover state (only when not selected) */
  &:hover {
    background-color: ${({ $isSelected }) =>
      $isSelected
        ? cssIngka.colourStaticIkeaBrandBlue
        : cssIngka.colourNeutral2};
    border-color: ${({ $isSelected }) =>
      $isSelected
        ? cssIngka.colourStaticIkeaBrandBlue
        : cssIngka.colourNeutral5};
  }

  /* Focus state for keyboard navigation - scoped to inner input */
  &:focus-within {
    outline: 2px solid ${cssIngka.colourStaticIkeaBrandBlue};
    outline-offset: 2px;
  }
`;

const StyledRadioInput = styled.input`
  margin-right: 8px;
  cursor: pointer;
  appearance: none;
  width: 16px;
  height: 16px;
  border: 2px solid ${cssIngka.colourNeutral5};
  border-radius: 50%;
  background-color: ${cssIngka.colourNeutral1};
  flex-shrink: 0;

  /* Hover state */
  &:hover {
    border-color: ${cssIngka.colourStaticIkeaBrandBlue};
  }

  /* Focus state */
  &:focus-visible {
    outline: 2px solid ${cssIngka.colourStaticIkeaBrandBlue};
    outline-offset: 2px;
  }

  /* Checked state */
  &:checked {
    background-color: ${cssIngka.colourStaticIkeaBrandBlue};
    border-color: ${cssIngka.colourStaticIkeaBrandBlue};
    position: relative;
  }

  /* Checkmark for checked state */
  &:checked::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 6px;
    height: 6px;
    background-color: ${cssIngka.colourNeutral1};
    border-radius: 50%;
    transform: translate(-50%, -50%);
  }
`;

// FieldValues that existing both in the FE form and BE customer
export interface CommonFieldValues {
  firstName: string;
  lastName: string;
  streetAddress: string;
  streetAddress2: string;
  streetAddress3: string;
  city: string;
  district: string;
  postCode: string;
  blockNumber: string;
  country: string;
  state: string;
  reason: string;
}

export interface FormFieldValues extends CommonFieldValues {
  updateProfileCheckbox: boolean;
  email: string;
  phoneNumber: string;
  terms: boolean;
  turnstileToken?: string;
}

export interface FormProps {
  isLoading: boolean;
  values: FormFieldValues;
  isAuthenticated: boolean;
  onSubmit: (fieldValues: FormFieldValues) => void;
}

export interface DropdownWithValueProps {
  name: string;
  value: string;
}

const formatStateAndCitiesObj = (obj: object) => {
  if (!obj) return obj;

  if (Array.isArray(obj))
    return obj.map((state) => {
      return {
        name: state,
        value: state,
      };
    });

  return Object.entries(obj).map((stateEntry: [string, string]) => {
    const [key, value] = stateEntry;
    return {
      name: value, // LOL!? I know, too much work to fix this shit
      value: key, // boooing
    };
  });
};

// Natural sort, sort an array of objects by name and numericals
const sortInAlphaNumOrder = (a: any, b: any) => {
  return a.name.localeCompare(b.name, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

/* global CSS_CONFIG */
const formSettings = CSS_CONFIG.FORMSETTINGS;
const formOrder = CSS_CONFIG.ORDER;
const Form: React.FC<FormProps> = ({
  values,
  onSubmit: triggerOnSubmit,
  isAuthenticated,
  isLoading,
}): JSX.Element => {
  const { turnstileToken } = useCart();

  const { t } = useTranslation();
  const [formValues, setFormValues] = useState<FormFieldValues>(values);
  const {
    showFullName,
    showFirstName,
    showLastName,
    streetAddress,
    streetAddress2,
    streetAddress3,
    states,
    showPostalCode,
    prefillPostalCode,
    showCountry,
    showCityDropdown,
    showDistrictDropdown,
    showBlockNumber,
    showPrivacyPolicyCheckBox,
    showAddressAutoCompleteHelpText,
    isFuttaimCountry,
    isAddressAutoComplete,
    combineBlockWithStreetAddress,
  } = formSettings;

  const cartContext = useCart();
  const items = cartContext.getItems();
  const orderLimit = CSS_CONFIG.ORDER_LIMIT || 5;
  const totalQuantity = items.reduce(
    (sum, cartItem) => sum + cartItem.quantity,
    0
  );
  const showConfirmationEmailLabel = totalQuantity > orderLimit;

  type TOGGLE_STATES = 'existing' | 'new';
  const [toggleState, setToggleState] = useState<TOGGLE_STATES>('existing');
  let addressAutoComplete = !isAddressAutoComplete ? false : !isAuthenticated;
  const announcementRef = useRef<HTMLDivElement>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  useEffect(() => {
    console.log('Form.tsx: TURNSTILE_TOKEN from context', turnstileToken);
  }, [turnstileToken]);

  const getOptions = (newOrExisting?: TOGGLE_STATES) => {
    return Object.entries(
      {
        new: {
          '1111': 'newDamaged',
          '1112': 'newDamagedPkg',
          '1122': 'newMissing',
          '1123': 'newWrong',
          '1141': 'newChange',
        },
        existing: {
          '2111': 'existingDamaged',
          '2112': 'existingReplace',
          '2121': 'existingRenew',
          '2123': 'existingAdjust',
          '2131': 'existingMissing',
        },
      }[newOrExisting || 'existing']
    );
  };

  const [listOfReasons, setListOfReasons] =
    useState<[string, string][]>(getOptions());
  const [isSubmitBtnClicked, setSubmitBtnClicked] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  let citiesList = formatStateAndCitiesObj(CSS_CONFIG.CITIES) || [];
  const sortedCitiesList = citiesList.sort(sortInAlphaNumOrder);
  const [cities, setCities] =
    useState<{ name?: string; value?: string }[]>(sortedCitiesList);
  const [district, setDistrict] = useState<{ name?: string; value?: string }[]>(
    []
  );

  // eslint-disable-next-line
  const setFormValuesByKey = (key: string, newValue: any): void => {
    setFormValues((prev) => {
      return {
        ...prev,
        [key]: newValue,
      };
    });
  };

  const onChangeVariable = (key: string) => {
    return (e: React.ChangeEvent<HTMLInputElement>): void => {
      const { target = {} } = e;
      const {
        value = '',
        checked = false,
        type = 'text',
      } = target as HTMLInputElement;
      const newValue = type === 'checkbox' ? checked : value;
      setFormValuesByKey(key, newValue);
    };
  };

  const onChangeStateDropdown = (key: string) => {
    return (e: React.ChangeEvent<HTMLInputElement>): void => {
      const { target = {} } = e;
      const { value = '' } = target as HTMLSelectElement;
      setFormValuesByKey(key, target.options[target.selectedIndex]?.value);
      (async (value: string) => {
        let citiesDropdown: DropdownWithValueProps[] = [];
        if (value) {
          const result = (await sparePartService.fetchCitiesFromState(
            value
          )) as CityResponseType[];
          citiesDropdown = result.map(
            ({ city, cityValue }: CityResponseType) => ({
              name: city,
              value: cityValue ? cityValue : city,
            })
          );
          if (citiesDropdown.length > 0) {
            citiesDropdown = citiesDropdown.sort(sortInAlphaNumOrder);
          }
        }
        setCities(citiesDropdown);
      })(value);
    };
  };

  const onChangeCityDropdown = (key: string) => {
    return (e: React.ChangeEvent<HTMLInputElement>): void => {
      const { target = {} } = e;
      const { value = '' } = target as HTMLSelectElement;
      setFormValuesByKey(key, target.options[target.selectedIndex]?.value);
      if (showDistrictDropdown) {
        (async (value: string) => {
          let districtDropdown: DropdownWithValueProps[] = [];
          if (value) {
            const result = (await sparePartService.fetchCitiesFromState(
              value
            )) as DistrictResponseType[];
            districtDropdown = result.map(
              ({ district, districtValue }: DistrictResponseType) => ({
                name: district,
                value: districtValue ? districtValue : district,
              })
            );
            if (districtDropdown.length > 0) {
              // sort array but ignore the last element.
              let lastElement =
                districtDropdown.pop() as DropdownWithValueProps;
              districtDropdown = districtDropdown.sort(sortInAlphaNumOrder);
              districtDropdown.push(lastElement);
            }
          }
          setDistrict(districtDropdown);
        })(value);
      }
    };
  };

  const onChangePostCode = (): void => {
    const newValue = formValues.postCode.replace(new RegExp('\\s', 'ig'), '');
    setFormValuesByKey('postCode', newValue);
  };

  const onChangeBlockNumber = (): void => {
    if (!formValues.blockNumber) return;

    const newValue = formValues.blockNumber.replace(
      new RegExp('\\s', 'ig'),
      ''
    );

    setFormValuesByKey('blockNumber', newValue);
  };

  const validatedFirstName = validateName(formValues.firstName, 49);
  const validatedLastName = validateName(formValues.lastName, 50);
  let validateAddrField1 = validatestreetAddress(formValues.streetAddress);
  //combined address1 with block number
  if (combineBlockWithStreetAddress && !validateAddrField1) {
    validateAddrField1 = validatestreetAddress(
      formValues.streetAddress + ' ' + formValues.blockNumber
    );
  }
  const validateAddrField2 = validatestreetAddress2(formValues.streetAddress2);
  const validateAddrField13 = validateInvalidCharacters(
    formValues.streetAddress3,
    50,
    true
  );
  const validatedCity = validateCity(formValues.city);
  const validatedDistrict = validateDistrict(formValues.district);
  const validatedState = validateState(formValues.state);
  const validatedReason = validateReason(formValues.reason);
  const validatedPostCode = validateZip(formValues.postCode);
  const validatedBlockNumber = validateBlockNumber(formValues.blockNumber);
  const validatedEmail = validateEmail(formValues.email);
  const validatedPhoneNumber = validatePhoneNumber(
    formValues.phoneNumber,
    CSS_CONFIG.COUNTRY
  );
  const validatedTerms = formValues.terms ? null : 'need_to_validate_terms';
  const showTOSCheckbox = !isAuthenticated && showPrivacyPolicyCheckBox;
  const { FULLWIDTH, FORMREQUIRED, COUNTRY, LANGUAGE } = CSS_CONFIG;
  const isSaudiMarket = COUNTRY.toLowerCase() === 'sa';
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [isAddressLookupComplete, setIsAddressLookupComplete] = useState(
    !addressAutoComplete || !isSaudiMarket
  );

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setSubmitBtnClicked(true);

    const isFormValid =
      [
        validatedFirstName,
        !isFuttaimCountry && validatedLastName,
        streetAddress && validateAddrField1,
        streetAddress2 && validateAddrField2,
        streetAddress3 && validateAddrField13,
        validatedCity,
        validatedPostCode,
        validatedBlockNumber,
        validatedEmail,
        validatedPhoneNumber,
        states && validatedState,
        showTOSCheckbox && validatedTerms,
        validatedReason,
        addressAutoComplete && isSaudiMarket && !isAddressLookupComplete,
      ].filter(Boolean).length === 0; // All resolved to null/false
    if (isFormValid) {
      setPendingSubmit(true);
      turnstileRef.current?.execute();
      let tempFormValues = formValues;
      if (formSettings.combineBlockWithStreetAddress) {
        tempFormValues = {
          ...formValues,
          ...appendBlock(formValues, COUNTRY),
        };
      }
      if (formSettings.useReMappingToBE) {
        tempFormValues = {
          ...formValues,
          ...renameObjectKeys(formValues, COUNTRY),
        };
        tempFormValues = {
          ...remapValuesToReadable(tempFormValues, COUNTRY),
        };
      }
      triggerOnSubmit({ ...tempFormValues, turnstileToken });
    } else {
      setSubmitBtnClicked(true);
    }
  };

  const handleTurnstileSuccess = (token: string) => {
    dispatchTurnstileToken(token);

    if (pendingSubmit) {
      let tempFormValues = formValues;

      if (formSettings.combineBlockWithStreetAddress) {
        tempFormValues = { ...formValues, ...appendBlock(formValues, COUNTRY) };
      }
      if (formSettings.useReMappingToBE) {
        tempFormValues = {
          ...formValues,
          ...renameObjectKeys(formValues, COUNTRY),
        };
        tempFormValues = { ...remapValuesToReadable(tempFormValues, COUNTRY) };
      }

      triggerOnSubmit({ ...tempFormValues, turnstileToken: token });
      setPendingSubmit(false);
    }
  };

  const country = getCountryDisplayName(LANGUAGE, COUNTRY);
  const fields = [
    {
      id: 'orderReason',
      label: t('orderForm.reasons.labelSelect'),
      type: 'dropdown-with-value',
      req: true,
      order: 0,
      value: formValues.reason,
      validatedField: validatedReason,
      shouldRender: true,
      options: listOfReasons.map((list: [string, string]) => {
        const [value, name] = list;
        return {
          name: t(`orderForm.reasons.${toggleState}.${name}`),
          value,
        };
      }),
      onChange: onChangeVariable('reason'),
      $fullwidth: true,
    },
    {
      id: 'first-name',
      label: t('orderForm.firstNameLabel'),
      value: formValues.firstName,
      onChange: onChangeVariable('firstName'),
      validatedField: validatedFirstName,
      shouldRender: showFirstName,
      $fullwidth: showFullName,
      order: formOrder.firstName,
      req: FORMREQUIRED.firstName,
      autocomplete: 'given-name',
    },
    {
      id: 'last-name',
      label: t('orderForm.lastNameLabel'),
      value: formValues.lastName,
      onChange: onChangeVariable('lastName'),
      validatedField: validatedLastName,
      shouldRender: showLastName,
      order: formOrder.lastName,
      req: FORMREQUIRED.lastName,
      autocomplete: 'family-name',
    },
    {
      id: 'address-line1',
      label: t('orderForm.addressLabel'),
      name: 'streetAddress',
      value: formValues.streetAddress,
      onChange: onChangeVariable('streetAddress'),
      validatedField: validateAddrField1,
      $fullwidth: FULLWIDTH.streetAddress,
      shouldRender: !addressAutoComplete && streetAddress,
      order: formOrder.streetAddress,
      req: FORMREQUIRED.streetAddress,
      autocomplete: 'address-line1',
    },
    {
      id: 'address-line2',
      label: t('orderForm.address2Label'),
      value: formValues.streetAddress2,
      onChange: onChangeVariable('streetAddress2'),
      validatedField: validateAddrField2,
      $fullwidth: FULLWIDTH.streetAddress2,
      shouldRender: !addressAutoComplete && streetAddress2,
      order: formOrder.streetAddress2,
      req: FORMREQUIRED.streetAddress2,
      autocomplete: 'address-line2',
    },
    {
      id: 'address-line3',
      label: t('orderForm.address3Label'),
      value: formValues.streetAddress3,
      onChange: onChangeVariable('streetAddress3'),
      validatedField: validateAddrField13,
      $fullwidth: FULLWIDTH.streetAddress3 || false,
      shouldRender: !addressAutoComplete && streetAddress3,
      order: formOrder.streetAddress3,
      autocomplete: 'address-line3',
    },
    {
      id: 'postal-code',
      label:
        COUNTRY === 'in'
          ? t('orderForm.postCodeLabelIndia')
          : t('orderForm.postCodeLabel'),
      value: formValues.postCode,
      onChange: onChangeVariable('postCode'),
      onBlur: onChangePostCode,
      validatedField: validatedPostCode,
      shouldRender: !addressAutoComplete && showPostalCode,
      order: formOrder.postCode,
      $fullwidth: FULLWIDTH.postCode,
      req: FORMREQUIRED.postalCode,
      autocomplete: 'postal-code',
    },
    {
      id: 'block-number',
      label: t('orderForm.blockNumberLabel'),
      value: formValues.blockNumber,
      onChange: onChangeVariable('blockNumber'),
      onBlur: onChangeBlockNumber,
      validatedField: validatedBlockNumber,
      shouldRender: !addressAutoComplete && showBlockNumber,
      order: formOrder.blockNumber,
      $fullwidth: FULLWIDTH.blockNumber,
      req: FORMREQUIRED.blockNumber,
      autocomplete: 'Block',
    },
    {
      id: 'city',
      label: t('orderForm.cityLabel'),
      value: formValues.city,
      onChange: onChangeVariable('city'),
      validatedField: validatedCity,
      shouldRender: !addressAutoComplete && !showCityDropdown,
      order: formOrder.city,
      $fullwidth: FULLWIDTH.city,
      req: !showCityDropdown && FORMREQUIRED.city,
      autocomplete: 'address-level2',
    },
    {
      id: 'city-dropdown',
      label: t('orderForm.cityLabel'),
      value: formValues.city,
      type: 'dropdown-with-value',
      onChange: onChangeCityDropdown('city'),
      validatedField: validatedCity,
      shouldRender: !addressAutoComplete && showCityDropdown,
      disabled: cities.length === 0,
      order: formOrder.city,
      $fullwidth: FULLWIDTH.city,
      options: cities.length > 0 ? cities.sort() : [{ name: '', value: '' }],
      req: showCityDropdown && FORMREQUIRED.city,
      autocomplete: 'address-level2',
    },
    {
      id: 'email',
      label: t('orderForm.emailLabel'),
      value: formValues.email,
      onChange: onChangeVariable('email'),
      validatedField: validatedEmail,
      shouldRender: true,
      $fullwidth: FULLWIDTH.email,
      order: formOrder.email,
      req: FORMREQUIRED.email,
      autocomplete: 'email',
    },
    {
      id: 'phone-number',
      label: t('orderForm.phoneNumberLabel'),
      value: formValues.phoneNumber,
      onChange: onChangeVariable('phoneNumber'),
      onBlur: (): void => {
        const formattedNumber = autoFormatNumber(
          formValues.phoneNumber,
          CSS_CONFIG.COUNTRYCALLINGCODE
        );
        setFormValuesByKey('phoneNumber', formattedNumber);
      },
      validatedField: validatedPhoneNumber,
      $fullwidth: FULLWIDTH.phoneNumber,
      shouldRender: true,
      order: formOrder.phoneNumber,
      autocomplete: 'tel',
    },
    {
      id: 'state',
      label: t('orderForm.stateLabel'),
      type: 'dropdown-with-value',
      value: formValues.state,
      order: formOrder.state,
      onChange: onChangeStateDropdown('state'),
      validatedField: validatedState,
      options: formatStateAndCitiesObj(CSS_CONFIG.STATES).sort(
        sortInAlphaNumOrder
      ),
      shouldRender: !addressAutoComplete && states,
      req: FORMREQUIRED.state,
      $fullwidth: FULLWIDTH.state,
      autocomplete: 'address-level1',
    },
    {
      id: 'district-dropdown',
      label: t('orderForm.districtLabel'),
      value: formValues.district,
      type: 'dropdown-with-value',
      onChange: onChangeVariable('district'),
      validatedField: validatedDistrict,
      shouldRender: !isAddressAutoComplete && showDistrictDropdown,
      disabled: district.length === 0,
      order: formOrder.district,
      $fullwidth: FULLWIDTH.district,
      options: district.length > 0 ? district : [{ name: '', value: '' }],
      req: showDistrictDropdown && FORMREQUIRED.district,
      autocomplete: 'address-level1',
    },
    {
      id: 'country',
      label: t('orderForm.countryLabel'),
      value: country,
      order: formOrder.country,
      disabled: true,
      validatedField: null,
      shouldRender: !isAddressAutoComplete && showCountry,
      req: FORMREQUIRED.country,
      $fullwidth: FULLWIDTH.country,
      autocomplete: 'address-level1',
    },
  ];

  useEffect(() => {
    setListOfReasons(getOptions(toggleState));
    setFormValuesByKey('reason', '');
    if (isFuttaimCountry) {
      setFormValuesByKey('postCode', '000');
    }

    if (
      prefillPostalCode &&
      prefillPostalCode.length > 0 &&
      !formValues.postCode
    ) {
      setFormValuesByKey('postCode', prefillPostalCode);
    }
  }, [toggleState]);

  // If not logged in, use default values
  if (!isAuthenticated && formValues.updateProfileCheckbox !== false) {
    setFormValuesByKey('updateProfileCheckbox', false);
  }
  const handleAddressChange = (details: AddressDetails) => {
    setFormValues((prev) => {
      return {
        ...prev,
        ['state']: details.state,
        ['streetAddress']: details.addressLine1,
        ['streetAddress2']: details.addressLine2,
        ['streetAddress3']: details.addressLine3,
        ['city']: details.city,
        ['postCode']: details.postalCode,
        ['country']: isSaudiMarket ? 'Saudi Arabia' : prev.country,
      };
    });
  };

  const handleLookupStateChange = (isSuccess: boolean) => {
    setIsAddressLookupComplete(isSuccess);
  };

  const announceSelection = (key: string) => {
    if (announcementRef.current) {
      announcementRef.current.textContent = `${t('orderForm.reasons.labelToggle')}. ${t(`orderForm.reasons.${key}`)} selected.`;
    }
  };

  const siteKey =
    process.env.NODE_ENV === 'development'
      ? process.env.GATSBY_TURNSTILE_TEST_SITE_KEY || ''
      : process.env.GATSBY_TURNSTILE_SITE_KEY || '';

  if (!siteKey) {
    console.warn('Missing GATSBY_TURNSTILE_SITE_KEY in environment variables');
  }

  // Handle radio input change for reason selector
  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === 'existing') {
      setToggleState('existing');
      setActiveIndex(0);
      announceSelection('toggleExistingProduct');
    } else if (value === 'new') {
      setToggleState('new');
      setActiveIndex(1);
      announceSelection('toggleNewProduct');
    }
  };

  return (
    <>
      <>
        <StyledH2>{t('orderForm.reasons.heading')}</StyledH2>
        <StyledFieldSet>
          <StyledLegend id="toggle-group-label">
            {t('orderForm.reasons.labelToggle')}
          </StyledLegend>

          {/* Live region for announcing selection changes */}
          <div
            ref={announcementRef}
            id="toggle-announcement"
            aria-live="polite"
            style={{ position: 'absolute', left: '-9999px' }}
          />

          <StyledRadioGroup
            role="radiogroup"
            aria-labelledby="toggle-group-label"
          >
            <StyledRadioLabel $isSelected={activeIndex === 0}>
              <StyledRadioInput
                type="radio"
                name="reason-selector"
                value="existing"
                checked={activeIndex === 0}
                onChange={handleReasonChange}
                aria-label={t('orderForm.reasons.toggleExistingProduct')}
              />
              {t('orderForm.reasons.toggleExistingProduct')}
            </StyledRadioLabel>
            <StyledRadioLabel $isSelected={activeIndex === 1}>
              <StyledRadioInput
                type="radio"
                name="reason-selector"
                value="new"
                checked={activeIndex === 1}
                onChange={handleReasonChange}
                aria-label={t('orderForm.reasons.toggleNewProduct')}
              />
              {t('orderForm.reasons.toggleNewProduct')}
            </StyledRadioLabel>
          </StyledRadioGroup>
        </StyledFieldSet>
      </>
      <form onSubmit={onSubmit} noValidate>
        <StyledInputFields>
          <RenderFields
            fields={fields}
            isSubmitBtnClicked={isSubmitBtnClicked}
            divideAfter="orderReason"
          />
          {isAuthenticated && (
            <CheckboxUpdateProfile
              checked={formValues.updateProfileCheckbox}
              onChange={onChangeVariable('updateProfileCheckbox')}
            />
          )}
        </StyledInputFields>
        {addressAutoComplete  && (
          <AddressAutoCompleteComponent
            onAddressSelect={handleAddressChange}
            submitAddressFlag={isSubmitBtnClicked}
            onLookupStateChange={handleLookupStateChange}
            fullwidth={FULLWIDTH.streetAddress3}
          />
        )}
        {addressAutoComplete && showAddressAutoCompleteHelpText !== false && (
          <StyledAddressContactUsWrapper>
            {t('spareParts.addressAutoCompleteHelpText')}
            <a
              href={`https://www.ikea.com/${COUNTRY}/${LANGUAGE}/customer-service/contact-us/`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('spareParts.contactUs')}
            </a>
          </StyledAddressContactUsWrapper>
        )}
        <StyledTOSWrapper>
          <TermsAndConditionsRenderer
            checked={formValues.terms}
            onChange={onChangeVariable('terms')}
            showCheckbox={showTOSCheckbox}
            isSubmitBtnClicked={isSubmitBtnClicked}
          />
          {showConfirmationEmailLabel && (
            <div style={{ marginTop: '1rem', marginBottom: '0.75rem' }}>
              <InlineMessage
                variant="informative"
                dismissable={false}
                subtle={false}
                ssrIcon={noticeCircle}
                body={t('orderForm.confirmationMail')}
              />
            </div>
          )}
        </StyledTOSWrapper>

        <Turnstile
          ref={turnstileRef}
          siteKey={siteKey}
          options={{ size: 'invisible' }}
          onSuccess={handleTurnstileSuccess}
          onError={(err) => {
            console.error('Turnstile error:', err);
          }}
        />
        <Button
          type="emphasised"
          htmlType="submit"
          text={t('orderForm.submitButtonText')}
          fluid
          loading={isLoading}
        />
      </form>
    </>
  );
};

const dispatchTurnstileToken = (token: string) => {
  window.dispatchEvent(new CustomEvent('turnstile-token', { detail: token }));
};

export default Form;
